import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import logo from "../../assets/images/logo.svg";
import vectorIcon from "../../assets/images/Vector.svg";
import chevronIcon from "../../assets/images/Chevron up.svg";

const NAV_SECTIONS = [
    {
        label: "Product",
        links: [
            { label: "MAIGrid", href: "/pages/Maigrid.html" },
            { label: "MAIGent", href: "/pages/Maigents.html" },
        ],
    },
    {
        label: "Resources",
        links: [
            { label: "Our Methodology", href: "/pages/OurMethodology.html" },
        ],
    },
    {
        label: "Company",
        links: [
            { label: "About Us", href: "/pages/AboutUs.html" },
            { label: "Contact Us", href: "/contact-us" },
        ],
    },
];

const NavigationBar = ({ hideActions = false, transparent = false }) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [openSection, setOpenSection] = useState(null);
    const [panelRect, setPanelRect] = useState(null);

    const headerRef = useRef(null);   // the <header> itself
    const menuBtnRef = useRef(null);  // the "Menu" toggle button
    const panelRef = useRef(null);    // the portal-ed panel

    // Recompute where the panel should sit, based on the header's
    // real position on screen — this is what lets us render it via
    // a portal (immune to any ancestor's overflow/z-index) while
    // still having it appear directly under the navbar.
    const updatePanelPosition = useCallback(() => {
        if (headerRef.current) {
            const rect = headerRef.current.getBoundingClientRect();
            setPanelRect({ top: rect.bottom, left: rect.left, width: rect.width });
        }
    }, []);

    useEffect(() => {
        if (!menuOpen) return;
        updatePanelPosition();
        window.addEventListener("resize", updatePanelPosition);
        window.addEventListener("scroll", updatePanelPosition, true);
        return () => {
            window.removeEventListener("resize", updatePanelPosition);
            window.removeEventListener("scroll", updatePanelPosition, true);
        };
    }, [menuOpen, updatePanelPosition]);

    // Close on outside click — has to check both the header (logo/menu
    // button) AND the portal-ed panel, since the panel no longer lives
    // inside the header in the DOM tree.
    useEffect(() => {
        function handleClickOutside(e) {
            const inHeader = headerRef.current && headerRef.current.contains(e.target);
            const inPanel = panelRef.current && panelRef.current.contains(e.target);
            if (!inHeader && !inPanel) setMenuOpen(false);
        }
        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    // Close if the viewport grows back to desktop size.
    useEffect(() => {
        function handleResize() {
            if (window.innerWidth > 768) setMenuOpen(false);
        }
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const toggleMenu = () => setMenuOpen((open) => !open);
    const toggleSection = (label) =>
        setOpenSection((current) => (current === label ? null : label));
    const closeMenu = () => {
        setMenuOpen(false);
        setOpenSection(null);
    };

    return (
        <header
            ref={headerRef}
            className={`new-navbar${transparent ? ' new-navbar--transparent' : ''}`}
        >
            <div className="new-navbar__container">

                {/* LEFT GROUP — logo + desktop nav links */}
                <div className="new-navbar__group">

                    <a href="/" className="new-navbar__logo-link">
                        <img src={logo} alt="Million AI logo" className="new-navbar__logo" />
                    </a>

                    {NAV_SECTIONS.map((section) => (
                        <div className="new-navbar__dropdown" key={section.label}>
                            <button type="button" className="new-navbar__link">
                                {section.label}
                                <img src={chevronIcon} alt="" className="new-navbar__chevron" />
                            </button>
                            <ul className="new-navbar__submenu">
                                {section.links.map((link) => (
                                    <li key={link.label}>
                                        <a href={link.href}>{link.label}</a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}

                </div>

                {/* MOBILE — "Menu ∨" (hidden on desktop), next to the logo */}
                <button
                    ref={menuBtnRef}
                    type="button"
                    className={`new-navbar__mobile-menu-link${menuOpen ? ' is-open' : ''}`}
                    aria-expanded={menuOpen}
                    aria-controls="mobileMenuPanel"
                    onClick={toggleMenu}
                >
                    Menu
                    <img src={chevronIcon} alt="" />
                </button>

                {/* DESKTOP RIGHT (hidden on mobile, hidden on signup) */}
                {!hideActions && (
                    <div className="new-navbar__right">
                        <a href="/login" className="new-navbar__link new-navbar__link--login">
                            Log in
                        </a>
                        <a href="/signup-page" className="new-navbar__btn">
                            <span>Get early access</span>
                            <img src={vectorIcon} alt="" className="new-navbar__btn-icon" />
                        </a>
                    </div>
                )}

                {/* MOBILE RIGHT — Log in + compact CTA (hidden on desktop, hidden on signup) */}
                {!hideActions && (
                    <div className="new-navbar__mobile-right">
                        <a href="/login" className="new-navbar__mobile-login">
                            Log in
                        </a>
                        <a href="/signup-page" className="new-navbar__btn">
                            <span>Get early access</span>
                            <img src={vectorIcon} alt="" className="new-navbar__btn-icon" />
                        </a>
                    </div>
                )}

            </div>

            {/* MOBILE MENU PANEL — portal-ed to document.body so no
                ancestor's overflow/z-index/transform can clip or bury it.
                Positioned with inline styles computed from the header's
                real on-screen position. */}
            {menuOpen && panelRect && createPortal(
                <div
                    ref={panelRef}
                    id="mobileMenuPanel"
                    className="new-navbar__mobile-panel is-open"
                    style={{ top: panelRect.top, left: panelRect.left, width: panelRect.width }}
                >
                    <ul className="new-navbar__mobile-panel-list">
                        {NAV_SECTIONS.map((section) => (
                            <li
                                className={`new-navbar__mobile-panel-item${openSection === section.label ? ' is-open' : ''}`}
                                key={section.label}
                            >
                                <button
                                    type="button"
                                    className="new-navbar__mobile-panel-toggle"
                                    onClick={() => toggleSection(section.label)}
                                >
                                    {section.label}
                                    <img src={chevronIcon} alt="" className="new-navbar__chevron" />
                                </button>
                                <ul className="new-navbar__mobile-panel-submenu">
                                    {section.links.map((link) => (
                                        <li key={link.label}>
                                            <a href={link.href} onClick={closeMenu}>{link.label}</a>
                                        </li>
                                    ))}
                                </ul>
                            </li>
                        ))}
                    </ul>
                </div>,
                document.body
            )}
        </header>
    );
};

export default NavigationBar;